import React, { useState, useEffect } from 'react';
import { supabase } from '../../utils/supabase';
import { Eye, CheckCircle, XCircle, Clock, FileText } from 'lucide-react';

interface CustomLessonRequest {
  id: string;
  user_id: string;
  level: string;
  dutch_text: string;
  notes: string | null;
  status: 'pending' | 'in_progress' | 'completed' | 'rejected';
  admin_notes: string | null;
  lesson_id: string | null;
  created_at: string;
  updated_at: string;
  user_email?: string;
}

export function CustomLessonRequests() {
  const [requests, setRequests] = useState<CustomLessonRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'in_progress' | 'completed' | 'rejected'>('all');
  const [selectedRequest, setSelectedRequest] = useState<CustomLessonRequest | null>(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    loadRequests();
  }, [filter]);

  const loadRequests = async () => {
    try {
      setLoading(true);

      let query = supabase
        .from('custom_lesson_requests')
        .select('*')
        .order('created_at', { ascending: false });

      if (filter !== 'all') {
        query = query.eq('status', filter);
      }

      const { data, error } = await query;

      if (error) throw error;

      if (data) {
        const requestsWithEmails = await Promise.all(
          data.map(async (request) => {
            const { data: userData } = await supabase.auth.admin.getUserById(request.user_id);
            return {
              ...request,
              user_email: userData?.user?.email || 'Unknown'
            };
          })
        );
        setRequests(requestsWithEmails);
      }
    } catch (error) {
      console.error('Error loading custom lesson requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateRequestStatus = async (requestId: string, status: string, notes?: string) => {
    try {
      setUpdating(true);

      const updateData: any = {
        status,
        updated_at: new Date().toISOString()
      };

      if (notes !== undefined) {
        updateData.admin_notes = notes;
      }

      const { error } = await supabase
        .from('custom_lesson_requests')
        .update(updateData)
        .eq('id', requestId);

      if (error) throw error;

      await loadRequests();
      setSelectedRequest(null);
      setAdminNotes('');
    } catch (error) {
      console.error('Error updating request:', error);
      alert('Failed to update request');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      in_progress: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };

    const icons = {
      pending: Clock,
      in_progress: FileText,
      completed: CheckCircle,
      rejected: XCircle
    };

    const Icon = icons[status as keyof typeof icons] || Clock;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${styles[status as keyof typeof styles]}`}>
        <Icon className="h-4 w-4" />
        {status.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  const getPendingCount = () => requests.filter(r => r.status === 'pending').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Custom Lesson Requests</h2>
          <p className="text-sm text-gray-600 mt-1">
            {getPendingCount()} pending requests
          </p>
        </div>
        <div className="flex gap-2">
          {(['all', 'pending', 'in_progress', 'completed', 'rejected'] as const).map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === status
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {status === 'all' ? 'All' : status.replace('_', ' ')}
            </button>
          ))}
        </div>
      </div>

      {requests.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-600">No requests found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {requests.map((request) => (
            <div
              key={request.id}
              className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <span className="font-semibold text-lg">Level {request.level}</span>
                    {getStatusBadge(request.status)}
                  </div>
                  <p className="text-sm text-gray-600">
                    Requested by: {request.user_email}
                  </p>
                  <p className="text-xs text-gray-500">
                    {new Date(request.created_at).toLocaleString()}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setSelectedRequest(request);
                    setAdminNotes(request.admin_notes || '');
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <Eye className="h-4 w-4" />
                  View Details
                </button>
              </div>

              <div className="border-t pt-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Dutch Text Preview:</p>
                <p className="text-sm text-gray-600 line-clamp-3">
                  {request.dutch_text}
                </p>
                {request.notes && (
                  <p className="text-xs text-gray-500 mt-2 italic">
                    Note: {request.notes}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">Request Details</h3>
                <button
                  onClick={() => setSelectedRequest(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Request Information</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Level:</span>
                    <span className="ml-2 font-medium">{selectedRequest.level}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Status:</span>
                    <span className="ml-2">{getStatusBadge(selectedRequest.status)}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">User:</span>
                    <span className="ml-2 font-medium">{selectedRequest.user_email}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Requested:</span>
                    <span className="ml-2">{new Date(selectedRequest.created_at).toLocaleString()}</span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Dutch Text</h4>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">
                    {selectedRequest.dutch_text}
                  </p>
                  <p className="text-xs text-gray-500 mt-2">
                    {selectedRequest.dutch_text.length} characters
                  </p>
                </div>
              </div>

              {selectedRequest.notes && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">User Notes</h4>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-700">{selectedRequest.notes}</p>
                  </div>
                </div>
              )}

              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Admin Notes</h4>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  rows={4}
                  placeholder="Add notes about this request..."
                />
              </div>

              <div className="flex gap-3">
                {selectedRequest.status !== 'completed' && (
                  <button
                    onClick={() => updateRequestStatus(selectedRequest.id, 'completed', adminNotes)}
                    disabled={updating}
                    className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <CheckCircle className="h-5 w-5" />
                    Mark Completed
                  </button>
                )}
                {selectedRequest.status === 'pending' && (
                  <button
                    onClick={() => updateRequestStatus(selectedRequest.id, 'in_progress', adminNotes)}
                    disabled={updating}
                    className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <FileText className="h-5 w-5" />
                    Start Working
                  </button>
                )}
                {selectedRequest.status !== 'rejected' && (
                  <button
                    onClick={() => updateRequestStatus(selectedRequest.id, 'rejected', adminNotes)}
                    disabled={updating}
                    className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <XCircle className="h-5 w-5" />
                    Reject
                  </button>
                )}
                <button
                  onClick={() => updateRequestStatus(selectedRequest.id, selectedRequest.status, adminNotes)}
                  disabled={updating}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Save Notes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
